import { Fragment, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
  CommandSeparator,
} from "../../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { SelectProps, Option } from "./types";
import { cn } from "../../utils";
import {
  findOptionByValue,
  getFlatSelectableOptions,
  getDisplayValues,
  getIconByValue,
  getLabelByValue,
  getLeafValues,
  getSelectedValuesArray,
  selectAllDescendantsOptions,
  unselectAllDescendantsOptions,
  updateParentStates,
} from "./utils";
import { OptionItem } from "./parts";
import { SELECT_MODE, SELECT_VARIANT } from "./consts";

export function Select({
  value,
  options = [],
  onSelect: propsOnSelect,
  disabled = false,
  readOnly = false,
  mode = SELECT_MODE.SINGLE,
  placeholder = "Select...",
  inputPlaceholder = "Search...",
  emptyState = "No option found.",
  triggerClassName,
  triggerWrapperClassName,
  valueClassName,
  searchable = false,
  shouldFilter = true,
  toggleIcon = <ChevronDown />,
  dir = "ltr",
  variant = "default",
  iconPosition = "right",
  selectionIndicator = "checkmark",
  optionVariant = "default",
  triggerWidth = "full",
  onSearchChange,
  renderValue,
  size = "default",
  contentMaxWidth,
  contentAlign = "center",
  contentSide,
  contentOffset,
  contentTopOffset,
  error,
  renderFooter,
  openOnHover = false,
  open: controlledOpen,
  onOpenChange: onOpenChangeProp,
  onEscapeKeyDown,
  onInteractOutside,
  onExplicitClose,
  ...rest
}: SelectProps) {
  const [measuredTriggerWidth, setMeasuredTriggerWidth] = useState(0);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpenControlled = controlledOpen !== undefined;
  const open = isOpenControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isOpenControlled) setInternalOpen(next);
    onOpenChangeProp?.(next);
  };
  const closeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const isMultipleMode = mode === SELECT_MODE.MULTIPLE;

  const [selected, setSelected] = useState<string | string[]>(
    value !== undefined ? value : isMultipleMode ? [] : "",
  );

  const preparedValueForSet: string[] = getSelectedValuesArray(selected);
  const selectedSet = new Set(preparedValueForSet);

  useEffect(() => {
    if (triggerRef.current) {
      setMeasuredTriggerWidth(triggerRef.current.offsetWidth);
    }
  }, [selected]);

  const labelByValue = getLabelByValue(options);
  const iconByValue = getIconByValue(options);

  useEffect(() => {
    const isControlledValue = value !== undefined;
    if (!isControlledValue) return;

    if (isMultipleMode) {
      setSelected((value as string[] | undefined) || []);
    } else {
      setSelected((value as string | undefined) || "");
    }
  }, [value, isMultipleMode]);

  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  const onHoverOpen = () => {
    if (!openOnHover || isInteractionDisabled) return;

    clearTimeout(closeTimerRef.current);
    setOpen(true);
  };

  const onHoverClose = () => {
    if (!openOnHover) return;

    closeTimerRef.current = setTimeout(() => setOpen(false), 100);
  };

  function onSelect(optionValue: string, isParent: boolean): void {
    if (!isMultipleMode) {
      setSelected(optionValue);
      // Call with string for single mode
      (propsOnSelect as ((value: string) => void) | undefined)?.(optionValue);
      setOpen(false);
      return;
    }

    setSelected((current: string | string[]) => {
      const currentArray = Array.isArray(current) ? current : [];
      const selectedSet = new Set(currentArray);
      const option = findOptionByValue(options, optionValue);

      if (!option) return currentArray;

      if (isParent) {
        onParentSelect(option, selectedSet);
      } else {
        onLeafSelect(optionValue, selectedSet);
      }

      const selectedArray = Array.from(selectedSet);
      // Call with array for multiple mode
      (propsOnSelect as ((value: string[]) => void) | undefined)?.(
        selectedArray,
      );
      return selectedArray;
    });
  }

  // Handles selection of a parent option, managing all its descendants
  function onParentSelect(option: Option, selectedSet: Set<string>): void {
    const leafValues = getLeafValues(option);
    const areAllOptionsSelected = leafValues.every(value =>
      selectedSet.has(value),
    );

    if (areAllOptionsSelected) {
      unselectAllDescendantsOptions(option, selectedSet);
    } else {
      selectAllDescendantsOptions(option, selectedSet);
    }
  }

  // Handles selection of a leaf option
  function onLeafSelect(optionValue: string, selectedSet: Set<string>): void {
    if (selectedSet.has(optionValue)) {
      selectedSet.delete(optionValue);
    } else {
      selectedSet.add(optionValue);
    }
    updateParentStates(options, selectedSet);
  }

  const renderTriggerContent = (): JSX.Element | JSX.Element[] => {
    const displayValues = getDisplayValues(options, preparedValueForSet);
    const isEmptyDisplay = displayValues.length === 0;
    const textSizeClassName = size === "xs" ? "text-xs" : "text-sm";

    if (renderValue) {
      const custom = renderValue({ displayValues });

      if (custom) {
        return custom as JSX.Element | JSX.Element[];
      }
    }

    if (isEmptyDisplay)
      return (
        <span
          className={cn(
            textSizeClassName,
            "font-normal",
            "text-text-input-neutral-placeholder",
            {
              "text-text-input-neutral-disabled": disabled || readOnly,
            },
            valueClassName,
          )}
        >
          {placeholder}
        </span>
      );

    return displayValues.map(value => {
      const icon = iconByValue.get(value);
      const label = labelByValue.get(value) || value;
      return (
        <span
          key={value}
          className={cn(
            textSizeClassName,
            "font-normal inline-flex items-center gap-1",
            "text-text-input-neutral-idle",
            {
              "text-text-input-neutral-disabled": disabled || readOnly,
            },
            valueClassName,
          )}
        >
          {icon && <span className="shrink-0">{icon}</span>}
          {label}
        </span>
      );
    });
  };

  const onValueChange = (value: string) => {
    setSearch(value);
    onSearchChange?.(value);
  };

  const renderCommandInputIfNeeded = () => {
    if (searchable) {
      return (
        <CommandInput
          className="text-text-interactive-neutral-active"
          placeholder={inputPlaceholder}
          value={search}
          onValueChange={onValueChange}
        />
      );
    }
    return undefined;
  };

  const renderFooterIfNeeded = () => {
    if (!renderFooter) {
      return <></>;
    }

    const onClose = () => {
      setOpen(false);
      onExplicitClose?.();
    };

    return (
      <div className="border-t border-t-border-interactive-neutral-idle">
        {renderFooter({ onClose })}
      </div>
    );
  };

  const isInteractionDisabled = disabled || readOnly;

  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (isInteractionDisabled || isMultipleMode || open) return;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();

      const flatOptions = getFlatSelectableOptions(options);
      if (flatOptions.length === 0) return;

      const currentValue = typeof selected === "string" ? selected : "";
      const currentIndex = flatOptions.findIndex(
        opt => opt.value === currentValue,
      );

      let nextIndex: number;
      if (currentIndex === -1) {
        nextIndex = e.key === "ArrowDown" ? 0 : flatOptions.length - 1;
      } else if (e.key === "ArrowDown") {
        nextIndex = Math.min(currentIndex + 1, flatOptions.length - 1);
      } else {
        nextIndex = Math.max(currentIndex - 1, 0);
      }

      const nextOption = flatOptions[nextIndex];
      setSelected(nextOption.value);
      (propsOnSelect as ((value: string) => void) | undefined)?.(
        nextOption.value,
      );
    }
  };

  return (
    <Popover
      open={open}
      onOpenChange={isInteractionDisabled ? undefined : setOpen}
    >
      <PopoverTrigger asChild disabled={isInteractionDisabled}>
        <div
          onKeyDown={handleTriggerKeyDown}
          className={cn(
            "flex items-center gap-1.5 justify-between min-w-24 border rounded-sm scrollbar-thin",
            "border-border-interactive-neutral-idle aria-invalid:border-destructive!",
            "aria-invalid:!ring-destructive/20 dark:aria-invalid:!ring-destructive/40",
            {
              "w-full": triggerWidth === "full",
              "w-fit": triggerWidth === "fit",
              "h-7 px-2 py-1.5 text-xs": size === "xs",
              "h-8 px-3 py-1": size === "default",
              "cursor-pointer hover:border-border-input-neutral-hover":
                !isInteractionDisabled,
              "hover:bg-fill-input-neutral-hover":
                !isInteractionDisabled && variant !== SELECT_VARIANT.GHOST,
              "bg-fill-input-neutral-idle":
                variant !== SELECT_VARIANT.GHOST && !readOnly,
              "bg-fill-input-neutral-disabled":
                readOnly && variant !== SELECT_VARIANT.GHOST,
              "cursor-not-allowed opacity-75": disabled,
              "cursor-default border-border-input-neutral-disabled":
                readOnly && !disabled,
              "flex-row-reverse": iconPosition === "left",
            },
            triggerWrapperClassName,
          )}
          ref={triggerRef}
          aria-disabled={isInteractionDisabled}
          aria-readonly={readOnly}
          aria-invalid={error || undefined}
          onMouseEnter={onHoverOpen}
          onMouseLeave={onHoverClose}
          {...rest}
        >
          <div
            className={cn(
              "flex justify-start text-start whitespace-nowrap overflow-x-auto gap-1.5",
              {
                "w-full": triggerWidth === "full",
                "w-fit": triggerWidth === "fit",
              },
              triggerClassName,
            )}
          >
            {renderTriggerContent()}
          </div>
          <div
            className={cn("[&>svg]:size-4", {
              "[&>svg]:text-icon-input-neutral-idle [&>svg]:hover:text-icon-input-neutral-hover":
                !isInteractionDisabled,
              "[&>svg]:text-icon-input-neutral-disabled": isInteractionDisabled,
            })}
          >
            {toggleIcon}
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 border bg-fill-interactive-neutral-idle border-border-interactive-neutral-idle [&>svg]:text-purple-muted-300"
        align={contentAlign}
        side={contentSide}
        sideOffset={contentOffset}
        onMouseEnter={onHoverOpen}
        onMouseLeave={onHoverClose}
        onEscapeKeyDown={onEscapeKeyDown}
        onInteractOutside={onInteractOutside}
        style={{
          width: "max-content",
          minWidth: measuredTriggerWidth ?? "auto",
          maxWidth: contentMaxWidth ?? "calc(100vw - 2rem)",
          ...(contentTopOffset && {
            transform: `translateY(${contentTopOffset}px)`,
          }),
        }}
      >
        <Command
          className="bg-fill-interactive-neutral-idle"
          shouldFilter={shouldFilter}
          {...(!searchable && { tabIndex: 0 })}
        >
          {renderCommandInputIfNeeded()}
          <CommandList>
            <CommandEmpty>{emptyState}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <Fragment key={option.value}>
                  <OptionItem
                    disabled={disabled}
                    option={option}
                    selected={selectedSet}
                    onSelect={onSelect}
                    dir={dir}
                    selectionIndicator={selectionIndicator}
                    size={size}
                    optionVariant={optionVariant}
                  />
                  {option.separator && (
                    <CommandSeparator className="bg-border-panel-neutral" />
                  )}
                </Fragment>
              ))}
            </CommandGroup>
            {renderFooterIfNeeded()}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
